import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ArrowRight } from "lucide-react";

const OWNER_EMAIL = "micmiccarlos@gmail.com";

function Brand() {
  return (
    <div>
      <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-none">
        SERATO <span className="text-orange-500">SALESCORE</span>
      </div>
      <div className="eyebrow !text-slate-400 mt-3">Powered by MSC Intelligence</div>
    </div>
  );
}

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" className="w-full h-11" onClick={go} data-testid="google-login-button">
      <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2"><path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.5-.2-2.2H12v4.2h5.9a5 5 0 0 1-2.2 3.3v2.7h3.5c2.1-1.9 3.3-4.7 3.3-8z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.4-2-6.3-4.7H2.1v2.8A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.7 14c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7H2.1a11 11 0 0 0 0 9.9L5.7 14z"/><path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.7l3.1-3.1A11 11 0 0 0 2.1 7l3.6 2.8C6.6 7.3 9.1 5.4 12 5.4z"/></svg>
      Continue with Google
    </Button>
  );
}

export default function Login() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [needsSetup, setNeedsSetup] = useState(null);
  const [form, setForm] = useState({ name: "Michael S. Carlos", email: OWNER_EMAIL, password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/setup-status").then((r) => setNeedsSetup(r.data.needs_setup)).catch(() => setNeedsSetup(false));
  }, []);

  if (user) return <Navigate to="/" replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (needsSetup && form.password !== form.confirm) return setError("Passwords do not match");
    setBusy(true);
    try {
      const { data } = needsSetup
        ? await api.post("/auth/setup", { name: form.name, email: form.email, password: form.password })
        : await api.post("/auth/login", { email: form.email, password: form.password });
      setUser(data);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white">
      <div className="relative hidden lg:flex flex-col justify-between bg-[#0B192C] p-14 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(90deg,#fff 0 1px,transparent 1px 64px),repeating-linear-gradient(0deg,#fff 0 1px,transparent 1px 64px)" }} />
        <div className="absolute -right-24 top-1/3 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />
        <div className="relative"><Brand /></div>
        <div className="relative max-w-md">
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white leading-tight">One pipeline for every roof Serato sells.</h1>
          <p className="text-slate-400 mt-5 leading-relaxed">Dealer & hardware distribution, commercial & industrial projects, and residential roofing — one CRM, one executive view.</p>
          <div className="mt-10 grid grid-cols-3 gap-3 text-xs text-slate-300">
            {["Dealer / Hardware", "Commercial & Industrial", "Residential"].map((m) => (
              <div key={m} className="border border-slate-700 rounded px-3 py-3"><span className="block h-1 w-6 bg-orange-500 mb-2" />{m}</div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© Serato Roofing · SalesCore V1</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid={needsSetup ? "setup-form" : "login-form"}>
          <div className="lg:hidden bg-[#0B192C] -mx-6 -mt-6 mb-6 p-6"><Brand /></div>
          {needsSetup === null ? null : needsSetup ? (
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-orange-600 bg-orange-50 px-2.5 py-1 rounded"><ShieldCheck className="h-3.5 w-3.5" /> First-run setup</div>
              <h2 className="text-2xl font-bold mt-3">Create the Superadmin account</h2>
              <p className="text-sm text-slate-500 mt-1">This one-time screen locks permanently after the account is created.</p>
            </div>
          ) : (
            <div>
              <h2 className="text-2xl font-bold">Sign in</h2>
              <p className="text-sm text-slate-500 mt-1">Use the account provided by your administrator.</p>
            </div>
          )}
          {needsSetup && (
            <div className="space-y-1.5"><Label>Full name</Label><Input value={form.name} onChange={set("name")} required data-testid="setup-name-input" /></div>
          )}
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="login-email-input" /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={form.password} onChange={set("password")} required minLength={needsSetup ? 8 : 1} data-testid="login-password-input" /></div>
          {needsSetup && (
            <div className="space-y-1.5"><Label>Confirm password</Label><Input type="password" value={form.confirm} onChange={set("confirm")} required data-testid="setup-confirm-input" /></div>
          )}
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-semibold" data-testid="login-submit-button">
            {busy ? "Please wait…" : needsSetup ? "Create Superadmin" : "Sign in"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          {!needsSetup && (
            <>
              <div className="flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />or<span className="h-px flex-1 bg-slate-200" /></div>
              <GoogleButton />
              <p className="text-xs text-slate-400">Google sign-in works only for emails already registered by an administrator.</p>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
